import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import rosaryData from './data/rosary.json';
import Countdown from './components/Countdown';
import Prayer from './components/Prayer';
import Amen from './components/Amen';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { initGA, trackPageView } from './utils/analytics';
import './App.css';

// Configuration - easily changeable for testing
const SESSION_INTERVAL_MINUTES = 30; // Every 30 minutes on the half hour

// Component to track page views
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// Calculate next session time based on 30-minute intervals (on the hour and half hour)
const calculateNextSession = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const milliseconds = now.getMilliseconds();
  
  // Determine if we're in the first or second half of the hour
  const isFirstHalf = minutes < 30;
  
  let nextSession;
  if (isFirstHalf) {
    // Next session is at :30 of current hour
    nextSession = new Date(now);
    nextSession.setMinutes(30, 0, 0);
  } else {
    // Next session is at :00 of next hour
    nextSession = new Date(now);
    nextSession.setHours(nextSession.getHours() + 1, 0, 0, 0);
  }
  
  return nextSession;
};

function App() {
  const [currentState, setCurrentState] = useState('landing'); // 'landing', 'countdown', 'prayer', 'amen'
  const [currentPrayerIndex, setCurrentPrayerIndex] = useState(0);

  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);
  const [currentPrayerRemainingTime, setCurrentPrayerRemainingTime] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [nextSessionTime, setNextSessionTime] = useState(calculateNextSession());
  
  // Check if user has already set audio preference
  const savedAudioPreference = localStorage.getItem('rosary-audio-enabled');
  const [audioEnabled, setAudioEnabled] = useState(savedAudioPreference === 'true');

  // Calculate current prayer index based on elapsed time since session start
  const calculateCurrentPrayer = (startTime) => {
    const now = new Date();
    const elapsedMs = now.getTime() - startTime.getTime();
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    
    let totalDuration = 0;
    for (let i = 0; i < rosaryData.length; i++) {
      const currentPrayerDuration = rosaryData[i].duration;
      totalDuration += currentPrayerDuration;
      
      if (elapsedSeconds < totalDuration) {
        // Calculate remaining time for just the current prayer
        const elapsedInCurrentPrayer = elapsedSeconds - (totalDuration - currentPrayerDuration);
        const remainingTime = currentPrayerDuration - elapsedInCurrentPrayer;
        
        
        return {
          index: i,
          remainingTime: Math.max(0, remainingTime)
        };
      }
    }
    
    // If we've completed all prayers
    return {
      index: rosaryData.length - 1,
      remainingTime: 0
    };
  };

  // Check if we should start a new session
  const checkForNewSession = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    
    // Calculate current session start time (either :00 or :30 of current hour)
    let currentSessionStart;
    if (minutes < 30) {
      // We're in the first half hour, session started at :00
      currentSessionStart = new Date(now);
      currentSessionStart.setMinutes(0, 0, 0);
    } else {
      // We're in the second half hour, session started at :30
      currentSessionStart = new Date(now);
      currentSessionStart.setMinutes(30, 0, 0);
    }
    
    // Only auto-start a session if we're not in countdown mode waiting for next session
    // or if we're in countdown mode but it's time for a new session to start
    const isTimeForNewSession = (minutes === 0 || minutes === 30) && now.getSeconds() < 5;
    
    if (!sessionStartTime || sessionStartTime.getTime() !== currentSessionStart.getTime()) {
      // Don't overwrite session/index when user is mid-prayer (e.g. just joined before :00/:30).
      // Prevents "right count, wrong prayer" when joining in the last second of a half-hour.
      if (currentState !== 'prayer') {
        setSessionStartTime(currentSessionStart);
        setNextSessionTime(calculateNextSession());
        const prayerInfo = calculateCurrentPrayer(currentSessionStart);
        setCurrentPrayerIndex(prayerInfo.index);
        setCurrentPrayerRemainingTime(prayerInfo.remainingTime);
      }

      // Determine current state
      const totalRosaryDuration = rosaryData.reduce((sum, prayer) => sum + prayer.duration, 0);
      const sessionElapsed = (now.getTime() - currentSessionStart.getTime()) / 1000;
      
      // Transition from countdown: allow when we're in the 5s window at session start,
      // or when we're already inside the new session (sessionElapsed < 60) so we don't
      // miss the transition if a tick is delayed (e.g. tab throttling, slow device).
      const inCountdownButSessionStarted = currentState === 'countdown' && sessionElapsed < 60;
      if (currentState !== 'countdown' || isTimeForNewSession || inCountdownButSessionStarted) {
        if (sessionElapsed < totalRosaryDuration) {
          setCurrentState('prayer');
        } else if (sessionElapsed < totalRosaryDuration + 60) { // 1 minute after rosary (updated from 300)
          setCurrentState('amen');
        } else {
          setCurrentState('countdown');
        }
      }
    }
  };

  // Update every second - but only if not on landing page
  useEffect(() => {
    if (currentState !== 'landing') {
      checkForNewSession();
      const interval = setInterval(checkForNewSession, 1000);
      return () => clearInterval(interval);
    }
  }, [currentState]);

  // Handle prayer completion
  const handlePrayerComplete = useCallback(() => {
    setCurrentPrayerIndex(prevIndex => {
      if (prevIndex < rosaryData.length - 1) {
        setCurrentPrayerRemainingTime(null);
        return prevIndex + 1;
      } else {
        // Rosary completed, show Amen page
        setCurrentState('amen');
        return prevIndex;
      }
    });
  }, []);

  // Handle Amen completion (after 5 minutes)
  const handleAmenComplete = useCallback(() => {
    setCurrentState('countdown');
  }, []);

  // Handle joining current session from landing page
  const handleJoinSession = useCallback(() => {
    const now = new Date();
    const minutes = now.getMinutes();
    
    // Calculate current session start time
    let currentSessionStart;
    if (minutes < 30) {
      currentSessionStart = new Date(now);
      currentSessionStart.setMinutes(0, 0, 0);
    } else {
      currentSessionStart = new Date(now);
      currentSessionStart.setMinutes(30, 0, 0);
    }
    
    setSessionStartTime(currentSessionStart);
    setNextSessionTime(calculateNextSession());
    
    // Calculate where we are in the current session
    const prayerInfo = calculateCurrentPrayer(currentSessionStart);
    setCurrentPrayerIndex(prayerInfo.index);
    setCurrentPrayerRemainingTime(prayerInfo.remainingTime);
    
    // Determine current state
    const totalRosaryDuration = rosaryData.reduce((sum, prayer) => sum + prayer.duration, 0);
    const sessionElapsed = (now.getTime() - currentSessionStart.getTime()) / 1000;
    
    if (sessionElapsed < totalRosaryDuration) {
      setCurrentState('prayer');
    } else if (sessionElapsed < totalRosaryDuration + 60) { // 1 minute after rosary (updated from 300)
      setCurrentState('amen');
    } else {
      setCurrentState('countdown');
    }
  }, []);

  // Handle waiting for next session from landing page
  const handleWaitForNext = useCallback(() => {
    // Clear any current session data
    setSessionStartTime(null);
    setCurrentPrayerIndex(0);
    
    // Set the next session time
    setNextSessionTime(calculateNextSession());
    
    // Go to countdown page
    setCurrentState('countdown');
  }, []);

  // Handle audio toggle
  const handleAudioToggle = useCallback((enabled) => {
    setAudioEnabled(enabled);
    localStorage.setItem('rosary-audio-enabled', enabled.toString());
  }, []);

  const renderCurrentState = useCallback(() => {
    switch (currentState) {
      case 'landing':
        return (
          <LandingPage
            onJoinSession={handleJoinSession}
            onWaitForNext={handleWaitForNext}
            audioEnabled={audioEnabled}
            onAudioToggle={handleAudioToggle}
          />
        );
      case 'countdown':
        return (
          <Countdown 
            nextSessionTime={nextSessionTime}
            sessionIntervalMinutes={SESSION_INTERVAL_MINUTES}
          />
        );
      case 'prayer':
        return (
          <Prayer
            key={sessionStartTime?.getTime() ?? 'no-session'}
            prayer={rosaryData[currentPrayerIndex]}
            prayerIndex={currentPrayerIndex}
            totalPrayers={rosaryData.length}
            onComplete={handlePrayerComplete}
            sessionStartTime={sessionStartTime}
            audioEnabled={audioEnabled}
            onAudioToggle={() => handleAudioToggle(!audioEnabled)}
            remainingTime={currentPrayerRemainingTime}
          />
        );
      case 'amen':
        return (
          <Amen
            onComplete={handleAmenComplete}
            sessionStartTime={sessionStartTime}
          />
        );
      default:
        return <LandingPage onJoinSession={handleJoinSession} onWaitForNext={handleWaitForNext} audioEnabled={audioEnabled} onAudioToggle={handleAudioToggle} />;
    }
  }, [currentState, nextSessionTime, currentPrayerIndex, handlePrayerComplete, sessionStartTime, audioEnabled, handleAudioToggle, handleAmenComplete, handleJoinSession, handleWaitForNext]);

  return (
    <Router>
      <PageTracker />
      <div className="App">
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/*" element={renderCurrentState()} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
